import React, { useCallback, useRef, useState } from "react";
import { Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { createSampleCabinetQuote } from "../service";

function resolveSampleProjectId(project) {
  const id = Number(project?.id ?? project?.project_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Prospect-only CTA to start a frozen sample cabinet quote, or open an existing one.
 * Post-login fulfillment runs in products.jsx (parent) via sessionStorage intent.
 * @param {{
 *   className?: string,
 *   layout?: "inline" | "sticky",
 *   visible?: boolean,
 *   sampleProject?: object | null,
 *   onAuthRequired?: () => void,
 *   isLoggedIn?: boolean,
 *   onQuoteCreated?: (project: object) => void,
 *   onError?: (message: string) => void,
 * }} props
 */
export default function StoreSampleCabinetCta({
  className = "",
  layout = "inline",
  visible = true,
  sampleProject = null,
  onAuthRequired,
  isLoggedIn = false,
  onQuoteCreated,
  onError,
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const createInFlightRef = useRef(false);
  const sampleProjectId = resolveSampleProjectId(sampleProject);
  const hasExistingSampleOrder = sampleProjectId != null;

  const createQuoteAndNavigate = useCallback(async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    setBusy(true);
    try {
      const res = await createSampleCabinetQuote();
      const body = res?.data ?? res;
      const project =
        body?.data?.project ?? body?.data ?? body?.project ?? body;
      const projectId = resolveSampleProjectId(project);
      onQuoteCreated?.(project);
      if (projectId != null) {
        navigate(`/projects/${projectId}`, { state: { project } });
      }
    } catch (e) {
      const message =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        "Unable to start sample cabinet quote.";
      onError?.(message);
    } finally {
      createInFlightRef.current = false;
      setBusy(false);
    }
  }, [navigate, onQuoteCreated, onError]);

  if (!visible) return null;

  const handleClick = async () => {
    if (!isLoggedIn) {
      onAuthRequired?.();
      return;
    }
    if (hasExistingSampleOrder) {
      navigate(`/projects/${sampleProjectId}`, {
        state: { project: sampleProject },
      });
      return;
    }
    if (busy || createInFlightRef.current) return;
    await createQuoteAndNavigate();
  };

  const layoutClassName =
    layout === "sticky"
      ? "w-100 mw-100 mx-0 mt-0 rounded-0 border-0 border-bottom px-3"
      : "mx-3 mt-2 border rounded-3 px-2";

  return (
    <div
      className={`d-flex align-items-center justify-content-between gap-2 py-2 ${layoutClassName} ${className}`.trim()}
      style={{
        background: "#FDEA9F",
        borderColor: "#fdea9f",
      }}
    >
      <div>
        <button
          type="button"
          className={`btn fw-semibold ${
            hasExistingSampleOrder
              ? "border-primary bg-white text-primary"
              : "btn-primary px-2"
          }`}
          disabled={busy}
          onClick={() => void handleClick()}
        >
          {busy ? <Spinner size="sm" /> : null}
          {hasExistingSampleOrder
            ? "View Sample Order"
            : "Order Sample Cabinet"}
        </button>{" "}
        {!hasExistingSampleOrder && (
          <span className="small text-primary fw-bold text-nowrap">for just ₹299</span>
        )}
      </div>
      <span
        className="small text-success text-nowrap fw-bold"
        style={{ fontSize: "0.8rem" }}
      >
        FREE Delivery
      </span>
    </div>
  );
}
